import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const FeedbackContext = createContext(null);

// Short confirmations ("Link copied") and yes/no questions ("Delete this
// draft?") shown inside the app instead of the browser's own pop-ups, which
// look out of place and can't be styled — especially on phones.
//
//   toast(message, { kind: "success" | "error" | "info", action })
//   const ok = await confirm({ title, text, confirmLabel, danger })
export function FeedbackProvider({ children }) {
  const { t } = useTranslation();
  const [toasts, setToasts] = useState([]);
  const [question, setQuestion] = useState(null);
  const confirmButton = useRef(null);
  const lastFocus = useRef(null);

  const dismiss = useCallback((id) => setToasts((list) => list.filter((item) => item.id !== id)), []);

  const toast = useCallback(
    (message, { kind = "success", action, duration = 3200 } = {}) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((list) => [...list.slice(-2), { id, message, kind, action }]);
      setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        lastFocus.current = document.activeElement;
        setQuestion({ ...options, resolve });
      }),
    []
  );

  const answer = useCallback(
    (value) => {
      setQuestion((current) => {
        current?.resolve(value);
        return null;
      });
      lastFocus.current?.focus?.();
    },
    []
  );

  useEffect(() => {
    if (!question) return undefined;
    confirmButton.current?.focus();
    const onKey = (event) => event.key === "Escape" && answer(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [question, answer]);

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((item) => (
          <div key={item.id} className={`toast toast--${item.kind}`}>
            <span className="toast__icon" aria-hidden="true">
              {item.kind === "error" ? "!" : item.kind === "info" ? "i" : "✓"}
            </span>
            <span className="toast__text">{item.message}</span>
            {item.action && (
              <button
                type="button"
                className="toast__action"
                onClick={() => {
                  item.action.onClick();
                  dismiss(item.id);
                }}
              >
                {item.action.label}
              </button>
            )}
          </div>
        ))}
      </div>

      {question && (
        <div className="sheet" onClick={() => answer(false)}>
          <div
            className="sheet__body"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-text"
            onClick={(event) => event.stopPropagation()}
          >
            <span className="sheet__grip" aria-hidden="true" />
            <h2 id="confirm-title">{question.title}</h2>
            {question.text && <p id="confirm-text">{question.text}</p>}
            <div className="sheet__actions">
              <button type="button" className="btn btn--outline btn--lg" onClick={() => answer(false)}>
                {question.cancelLabel || t("common.cancel")}
              </button>
              <button
                ref={confirmButton}
                type="button"
                className={`btn btn--lg ${question.danger ? "btn--danger-solid" : "btn--primary"}`}
                onClick={() => answer(true)}
              >
                {question.confirmLabel || t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </FeedbackContext.Provider>
  );
}

export const useFeedback = () => useContext(FeedbackContext);
